import React, { useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

export default function EnergiesReportDialog({
  open,
  onClose,
  deviceId,
  deviceType = "analyzer",
  API_BASE,
}) {
  const { t, i18n } = useTranslation();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [format, setFormat] = useState("csv");
  const [energyType, setEnergyType] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const energyOptions =
    deviceType?.toLowerCase() === "analyzer"
      ? [
        { value: "import-active", label: t('energyReports.importActive') },
        { value: "export-active", label: t('energyReports.exportActive') },
        { value: "import-reactive", label: t('energyReports.importReactive') },
        { value: "export-reactive", label: t('energyReports.exportReactive') },
        { value: "quad1", label: t('energyReports.quad1') },
        { value: "quad2", label: t('energyReports.quad2') },
        { value: "quad3", label: t('energyReports.quad3') },
        { value: "quad4", label: t('energyReports.quad4') },
        { value: "import-apparent", label: t('energyReports.importApparent') },
        { value: "export-apparent", label: t('energyReports.exportApparent') },
      ]
      : deviceType?.toLowerCase() === "gruparge"
        ? [
          { value: "import-active", label: t('energyReports.activeConsumption') },
          { value: "export-active", label: t('energyReports.activeProduction') },
          { value: "inductive", label: t('energyReports.inductiveEnergy') },
          { value: "capacitive", label: t('energyReports.capacitiveEnergy') },
        ]
        : [
          { value: "active", label: t('energyReports.activeEnergy') },
          { value: "inductive", label: t('energyReports.inductiveEnergy') },
          { value: "capacitive", label: t('energyReports.capacitiveEnergy') },
          { value: "apparent", label: t('energyReports.apparentEnergy') },
        ];

  const handleDownload = async () => {
    if (!energyType || !start || !end) {
      alert(t('energyReports.fillAll'));
      return;
    }

    try {
      setIsLoading(true);

      const endpoint =
        deviceType === "analyzer"
          ? `/reports/export-energy/${energyType}`
          : `/reports/export-reactive-energy/${energyType}`;

      const lang = i18n.language.startsWith("tr") ? "tr" : "en";
      const url = `${API_BASE}${endpoint}?deviceId=${deviceId}&deviceType=${deviceType}&start=${start}&end=${end}&format=${format}&lang=${lang}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(t('energyReports.errorFetch'));

      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `energy_${energyType}_${start}_${end}.${format}`;
      link.click();
      URL.revokeObjectURL(link.href);
      onClose();
    } catch (err) {
      alert(t('energyReports.errorCreate'));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (setter) => (e) => {
    setter(e.target.value);
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
            <Dialog.Title className="text-lg font-semibold text-gray-800 mb-4">
              {t('energyReports.energyReportTitle')}
            </Dialog.Title>

            {/* Enerji tipi seçimi */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-1">{t('energyReports.energyType')}</label>
              <select
                value={energyType}
                onChange={(e) => setEnergyType(e.target.value)}
                disabled={isLoading}
                className="w-full border rounded-md px-3 py-2 focus:ring focus:ring-sky-200 focus:border-sky-400"
              >
                <option value="">{t('energyReports.select')}</option>
                {energyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Tarih seçimi */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('energyReports.startDate')}</label>
                <input
                  type="date"
                  value={start}
                  onChange={handleDateChange(setStart)}
                  disabled={isLoading}
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('energyReports.endDate')}</label>
                <input
                  type="date"
                  value={end}
                  onChange={handleDateChange(setEnd)}
                  disabled={isLoading}
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>
            </div>

            {/* Format seçimi */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-1">{t('energyReports.format')}</label>
              <select
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                disabled={isLoading}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="csv">CSV</option>
                <option value="xlsx">{t('reports.excel')}</option>
                <option value="png">{t('reports.chart')} (PNG)</option>
              </select>
            </div>

            {/* Butonlar */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDownload}
                disabled={isLoading}
                className={`px-4 py-2 rounded-md text-white ${isLoading
                  ? "bg-sky-400 cursor-wait"
                  : "bg-sky-600 hover:bg-sky-700"
                  }`}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>
                    {t('energyReports.preparing')}
                  </span>
                ) : (
                  t('common.download')
                )}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition>
  );
}
